import React, { useState, useEffect } from 'react';
import {
  Row, Col, Form, FormInstance, DatePicker, Input, Select, Card, Typography,
  Button, TimePicker, notification, Spin, Switch, Tooltip,
} from 'antd';
import moment from 'moment';
import { QuestionCircleOutlined } from '@ant-design/icons';
import MaskedInput, { Mask } from 'react-text-mask';
import { useNavigate, useMatch } from 'react-router-dom';
import {
  get, post, put, isSuperAgentError, RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { getUserPermissions } from '../../../utils/users';
import { IBGEState, IBGECity } from '../../../sharedTypes/IBGETypes';
import { BranchCoopercitrus } from '../../../sharedTypes/BranchTypes';
import { fetchAllStates, fetchCitiesByState } from '../../../dataSource/APILocalidadesIBGE';
import { fetchCepDetails } from '../../../dataSource/APIViacep';
import './ManageEvent.scss';

const ManageEvent = (): React.ReactElement => {
  interface FormResult {
    name: string;
    description: string | undefined;
    date: moment.Moment;
    startTime: moment.Moment;
    endTime: moment.Moment;
    isCancelled: boolean | undefined;
    cep: string | undefined;
    state: string;
    city: string;
    district: string | undefined;
    streetAddress: string;
    addressComplement: string | undefined;
    addressNumber: string | undefined;
    subsidiary: Array<number | string> | undefined;
    culture: string;
    comments: string | undefined;
    visible?: boolean;
    link?: string;
    linkName?: string;
  }

  interface BranchCoopercitrusFromClient {
    branchCoopercitrus: string | undefined;
    center: string | undefined;
    department: string | undefined;
    id: number | string | undefined;
    name: string | undefined;
    pole: number | undefined;
    state: string | undefined;
    address: string | undefined;
    cep: string | undefined;
    city: string | undefined;
    complement: string | undefined;
    email: string | undefined;
    lat: string | undefined;
    log: string | undefined;
    phone: string | undefined;
    segment: string | undefined;
    uf: string | undefined;
  }

  const navigate = useNavigate();
  const [formInstance] = Form.useForm();
  const routeMatchEdit = useMatch('/events/:id/edit');

  const userPermissions = getUserPermissions();

  const [mode] = useState<'CREATE' | 'EDIT'>(!routeMatchEdit ? 'CREATE' : 'EDIT');
  const [eventDataLoading, setEventDataLoading] = useState<boolean>(mode === 'EDIT');

  const [subsidiaryLoading, setSubsidiaryLoading] = useState<boolean>(false);
  const [statesLoading, setStatesLoading] = useState<boolean>(false);
  const [citiesLoading, setCitiesLoading] = useState<boolean>(false);
  const [cepApiLoading, setCepApiLoading] = useState<boolean>(false);

  const [createRequestLoading, setCreateRequestLoading] = useState<boolean>(false);

  const [subsidiaries, setSubsidiaries] = useState<Array<BranchCoopercitrusFromClient>>([]);
  const [statesArray, setStatesArray] = useState<Array<IBGEState>>([]);
  const [citiesArray, setCitiesArray] = useState<Array<IBGECity>>([]);

  const handleStateAddressChange = async (stateValue: string): Promise<void> => {
    formInstance.setFieldsValue({
      city: undefined,
    });

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      setCitiesLoading(true);
      const requestRef: RequestWrapper = fetchCitiesByState({
        identifier: stateValue,
      });

      response = await requestRef;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
      console.log('erro fetchCitiesByState', error);
    }

    switch (true) {
      case (response?.statusCode === 200):
        setCitiesArray(response?.body);
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      case (responseError?.error):
      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao buscar as cidades do Estado selecionado.',
        });
        break;
    }

    setCitiesLoading(false);
  };

  useEffect(() => {
    const fetchSubsidiaries = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      try {
        setSubsidiaryLoading(true);

        const requestReference: RequestWrapper = get({
          url: '/branch/listAllBranchGuestUser',

          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await requestReference;
      } catch (error: unknown) {
        console.log('erro filiais', error);
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 13013):
          // "Branch guest user listed successfully", 13013
          setSubsidiaries(response?.body.listAllBranchGuestUserOutputs);
          break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -17001):
          // "Listed branch not found", -17001
          notification.info({
            message: 'Atenção',
            description: 'Não foram encontradas filiais ativas.',
          });
          setSubsidiaries([]);
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um problema ao buscar as filiais, por favor tente novamente mais tarde.',
          });
          break;
      }

      setSubsidiaryLoading(false);
    };

    const getStates = async (): Promise<void> => {
      let response;

      try {
        setStatesLoading(true);
        const requestRef: RequestWrapper = fetchAllStates();
        response = await requestRef;
      } catch (error: unknown) {
        console.log('getStates error', error);
      }

      switch (true) {
        case (response?.statusCode === 200):
          setStatesArray(response?.body);
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um problema ao buscar os Estados, por favor tente novamente mais tarde.',
          });
          break;
      }

      setStatesLoading(false);
    };

    const fetchEventDetails = async (eventID: string | number): Promise<void> => {
      if (!eventID) {
        throw new Error('id do evento não informado');
      }

      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      try {
        setEventDataLoading(true);

        const requestReference: RequestWrapper = get({
          url: `/event/findEventById/${eventID}`,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        response = await requestReference;
      } catch (error: unknown) {
        console.log('erro findEventById', error);
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 15005):
          formInstance.setFieldsValue({
            state: response?.body.eventOutput.uf,
          });

          await handleStateAddressChange(response?.body.eventOutput.uf);

          formInstance.setFieldsValue({
            name: response?.body.eventOutput.name,
            description: response?.body.eventOutput.description,
            date: moment(response?.body.eventOutput.initialDate).utc(),
            startTime: moment(response?.body.eventOutput.initialDate).utc(),
            endTime: moment(response?.body.eventOutput.finalDate).utc(),
            cep: response?.body.eventOutput.cep,
            city: response?.body.eventOutput.city,
            district: response?.body.eventOutput.district,
            streetAddress: response?.body.eventOutput.street,
            addressComplement: response?.body.eventOutput.complement,
            addressNumber: response?.body.eventOutput.number,
            isCancelled: response?.body.eventOutput.isCancelled,
            subsidiary: response
              ?.body
              .eventOutput
              .branchCoopercitrus.length === 0
              ? ['NONE']
              : response
                ?.body
                .eventOutput
                .branchCoopercitrus
                .map((element: BranchCoopercitrus) => {
                  return element.id;
                }),
            culture: response?.body.eventOutput.culture,
            comments: response?.body.eventOutput.comments,
            link: response?.body.eventOutput.link,
            linkName: response?.body.eventOutput.linkName,
            visible: response?.body.eventOutput.visible,
          });

          break;

        case (responseError?.code === -16001):
        case (response?.body?.code === -16001):
          notification.error({
            message: 'Erro',
            description: 'Evento não encontrado.',
          });

          navigate('/events');
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Desculpe, houve um problema ao buscar os dados do evento, por favor tente novamente mais tarde.',
          });

          navigate('/events');
          break;
      }

      setEventDataLoading(false);
    };

    const executeOnLoad = async (): Promise<void> => {
      await fetchSubsidiaries();
      await getStates();

      if (
        mode === 'EDIT'
        && routeMatchEdit
        && routeMatchEdit?.params?.id != null
      ) {
        await fetchEventDetails(routeMatchEdit.params.id);
      }
    };

    executeOnLoad();
    return () => {
      setStatesLoading(false);
    };
  }, []);

  const handleFormFinishCreate = async (formValues: FormResult): Promise<void> => {
    let createEventResponse: ResponseWrapper | undefined;
    let createEventResponseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    try {
      setCreateRequestLoading(true);

      let targetURLName = null;
      let targetURL = null;
      if (formValues.link != '' && formValues.link != undefined && formValues.link != null) {
        targetURL = (
          formValues?.link?.includes('http')
            ? formValues.link
            : `http://${formValues.link}`
        );
      }

      if (formValues.linkName != '' && formValues.linkName != undefined && formValues.linkName != null) {
        targetURLName = formValues.linkName;
      }

      if (targetURLName != null && targetURL == null) {
        notification.error({
          message: 'Erro',
          description: 'É obrigatório informar um link se você optou por adicionar uma descrição do link.',
        });

        setCreateRequestLoading(false);
        return;
      }

      const requestReference = post({
        url: '/event/requestEvent',
        params: {
          branchId: formValues.subsidiary?.includes('NONE') ? null : formValues.subsidiary,
          cep: formValues.cep,
          city: formValues.city,
          comments: formValues.comments,
          complement: formValues.addressComplement,
          culture: formValues.culture,
          description: formValues.description,
          district: formValues.district,
          name: formValues.name,
          number: formValues.addressNumber,
          place: formValues.streetAddress,
          street: formValues.streetAddress,
          uf: formValues.state,
          link: targetURL,
          linkName: targetURLName,
          visible: true,

          state: statesArray.find((stateElem) => {
            return stateElem?.sigla === formValues.state;
          })?.nome,

          eventDate: moment(
            `${formValues.date.format('YYYY-MM-DD[T]')}${formValues.startTime.format('HH:mm:ss.SSS[Z]')}`,
          ).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),

          finalDate: moment(
            `${formValues.date.format('YYYY-MM-DD[T]')}${formValues.endTime.format('HH:mm:ss.SSS[Z]')}`,
          ).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),

          initialDate: moment(
            `${formValues.date.format('YYYY-MM-DD[T]')}${formValues.startTime.format('HH:mm:ss.SSS[Z]')}`,
          ).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      createEventResponse = await requestReference;
    } catch (error: unknown) {
      console.log('erro requestEvent', error);
      if (isSuperAgentError(error)) {
        createEventResponseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (createEventResponse?.body?.code === 15000):
        notification.success({
          message: 'Sucesso',
          description: 'Evento cadastrado.',
        });

        navigate('/events');
        break;

      case (createEventResponseError?.body.code === -16000):
      case (createEventResponse?.body?.code === -16000):
        notification.error({
          message: 'Erro',
          description: 'A data final não pode ser anterior a data inicial.',
        });
        break;

      case (createEventResponseError?.body?.code === -4001):
        break;

      case (createEventResponseError?.body?.code === -4003):
        notification.error({
          message: 'Erro',
          description: 'Desculpe, você não tem permissão para executar essa operação',
        });
        break;

      case (createEventResponseError?.body?.code === -5031):
        notification.error({
          message: 'Erro',
          description: 'Unidade de negócios responsável é um campo obrigatório',
        });
        break;

      case (createEventResponseError?.body?.code === -1007):
        notification.error({
          message: 'Erro',
          description: 'Usuário não encontrado',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao cadastrar o evento, por favor tente novamente mais tarde.',
        });
        break;
    }

    setCreateRequestLoading(false);
  };

  const handleFormFinishEdit = async (formValues: FormResult): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    if (!routeMatchEdit || routeMatchEdit?.params?.id == null) {
      notification.error({
        message: 'Erro',
        description: 'Desculpe, houve um erro ao acessar os detalhes do evento.',
      });
      return;
    }

    try {
      setCreateRequestLoading(true);

      let targetURLName = null;
      let targetURL = null;
      if (formValues.link != '' && formValues.link != undefined && formValues.link != null) {
        targetURL = (
          formValues?.link?.includes('http')
            ? formValues.link
            : `http://${formValues.link}`
        );
      }

      if (formValues.linkName != '' && formValues.linkName != undefined && formValues.linkName != null) {
        targetURLName = formValues.linkName;
      }

      if (targetURLName != null && targetURL == null) {
        notification.error({
          message: 'Erro',
          description: 'É obrigatório informar um link se você optou por adicionar uma descrição do link.',
        });

        setCreateRequestLoading(false);
        return;
      }

      const requestReference = put({
        url: '/event/editEvent',

        params: {
          branchId: formValues.subsidiary?.includes('NONE') ? null : formValues.subsidiary,
          cep: formValues.cep,
          city: formValues.city,
          comments: formValues.comments,
          complement: formValues.addressComplement,
          culture: formValues.culture,
          description: formValues.description,
          district: formValues.district,
          name: formValues.name,
          number: formValues.addressNumber,
          place: formValues.streetAddress,
          street: formValues.streetAddress,
          uf: formValues.state,
          eventId: routeMatchEdit.params.id,
          isCancelled: formValues.isCancelled,
          visible: formValues.isCancelled === false ? true : formValues.visible,
          link: targetURL,
          linkName: targetURLName,

          state: statesArray.find((stateElem) => {
            return stateElem?.sigla === formValues.state;
          })?.nome,

          finalDate: moment(
            `${formValues.date.format('YYYY-MM-DD[T]')}${formValues.endTime.format('HH:mm:ss.SSS[Z]')}`,
          ).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),

          initialDate: moment(
            `${formValues.date.format('YYYY-MM-DD[T]')}${formValues.startTime.format('HH:mm:ss.SSS[Z]')}`,
          ).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),

          eventDate: moment(
            `${formValues.date.format('YYYY-MM-DD[T]')}${formValues.startTime.format('HH:mm:ss.SSS[Z]')}`,
          ).toISOString(),
        },

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      response = await requestReference;
    } catch (error: unknown) {
      console.log('erro editEvent', error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 15002):
        notification.success({
          message: 'Sucesso',
          description: 'Evento editado.',
        });

        navigate('/events');
        break;

      case (responseError?.body?.code === -4001):
        break;

      case (responseError?.body?.code === -4003):
        notification.warning({
          message: 'Atenção',
          description: 'Desculpe, você não possui permissão para realizar esta ação.',
        });
        break;

      case (responseError?.body?.code === -16001):
        notification.warning({
          message: 'Atenção',
          description: 'Evento não encontrado',
        });
        break;

      case (responseError?.body?.code === -5031):
        notification.error({
          message: 'Atenção',
          description: 'Unidade de negócios responsável (Filial) é um campo obrigatório',
        });
        break;

      case (responseError?.body?.code === -16002):
        notification.error({
          message: 'Atenção',
          description: 'Não foi possível editar pois o evento foi cancelado',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao editar o evento, por favor tenta novamente mais tarde.',
        });
        break;
    }

    setCreateRequestLoading(false);
  };

  const fetchCepData = async (cepValue: string): Promise<void> => {
    if (cepValue.length !== 8) {
      return;
    }

    let response;

    try {
      setCepApiLoading(true);
      const requestRef: RequestWrapper = fetchCepDetails({
        cep: cepValue,
        returnType: 'json',
      });

      response = await requestRef;

      switch (true) {
        case (response.statusCode === 200 && !('erro' in response.body)):
          formInstance.setFieldsValue({
            state: response.body.uf,
            streetAddress: response.body.logradouro,
            district: response.body.bairro,
          });

          await handleStateAddressChange(response.body.uf);
          formInstance.setFieldsValue({
            city: response.body.localidade,
          });

          break;

        case (response.body.erro === true):
        default:
          break;
      }
    } catch (error: unknown) {
      console.log('viacep response error', error);
      return;
    } finally {
      setCepApiLoading(false);
    }
  };

  const validateCep = async (_rule: any, value: string): Promise<Error | void> => {
    if (!value || value.length !== 8) {
      return Promise.resolve();
    }

    let response;

    try {
      setCepApiLoading(true);
      const requestRef: RequestWrapper = fetchCepDetails({
        cep: value,
        returnType: 'json',
      });

      response = await requestRef;

      switch (true) {
        case (response.statusCode === 200 && !('erro' in response.body)):
          return Promise.resolve();

        case (response.body.erro === true):
        default:
          return Promise.reject(new Error('CEP inválido'));
      }
    } catch (error: unknown) {
      console.log('viacep response error', error);
      return Promise.reject(new Error('Erro ao validar cep'));
    } finally {
      setCepApiLoading(false);
    }
  };

  return (
    <div
      className="ManageEvent"
    >
      <Spin
        spinning={eventDataLoading}
      >
        <Row
          gutter={[24, 24]}
          className="ManageEvent__ContainerRow"
        >
          <Col
            span={24}
            className="ManageEvent__ContainerColumn"
          >
            <Row
              gutter={[24, 24]}
              justify="space-between"
              className="ManageEvent__PageTitleRow"
            >
              <Col
                className="ManageEvent__PageTitleColumn"
              >
                <Typography.Title
                  className="ManageEvent__PageTitle"
                >
                  {
                    mode === 'CREATE' ? 'Cadastrar Evento' : 'Editar Evento'
                  }
                </Typography.Title>
              </Col>

              <Col
                xs={{ span: 24 }}
                sm={{ span: 8 }}
                lg={{ span: 4 }}
                className="ManageEvent__ButtonColumn"
              >
                <Button
                  type="default"
                  className="ManageEvent__BackButton"
                  onClick={
                    (): void => {
                      navigate('/events');
                    }
                  }
                  block
                >
                  Voltar
                </Button>
              </Col>
            </Row>

            <Row
              gutter={[24, 24]}
              className="ManageEvent__ContentRow"
            >
              <Col
                span={24}
                className="ManageEvent__ContentColumn"
              >
                <Card
                  className="ManageEvent__Card"
                >
                  <Form
                    form={formInstance}
                    layout="vertical"
                    className="ManageEvent__Form"
                    onFinish={
                      (formValues): void => {
                        if (mode === 'CREATE') {
                          handleFormFinishCreate(formValues);
                        } else {
                          handleFormFinishEdit(formValues);
                        }
                      }
                    }
                    onFinishFailed={
                      (errorInfo): void => {
                        console.log('create event form finish failed', errorInfo);
                      }
                    }
                    onValuesChange={
                      (changedValues): void => {
                        if ('state' in changedValues) {
                          handleStateAddressChange(changedValues.state);
                        }

                        if ('cep' in changedValues) {
                          fetchCepData(changedValues.cep);
                        }

                        if ('subsidiary' in changedValues) {
                          if (changedValues.subsidiary.includes('ALL')) {
                            formInstance.resetFields(['subsidiary']);
                            formInstance.setFieldsValue({
                              subsidiary: subsidiaries.map((elementSubsidiary) => {
                                return elementSubsidiary.id;
                              }),
                            });
                          }
                        }
                      }
                    }
                  >
                    <Row
                      gutter={[24, 24]}
                      className="ManageEvent__NameRow"
                    >
                      <Col
                        span={24}
                        className="ManageEvent__NameColumn"
                      >
                        <Form.Item
                          className="ManageEvent__NameFormItem"
                          name="name"
                          label={(
                            <Typography.Text
                              className="ManageEvent__NameLabel ManageEvent__AllFormLabels"
                            >
                              Nome do evento
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Nome do evento é obrigatório',
                            },
                            {
                              max: 255,
                              message: 'Nome deve possuir no máximo 255 caracteres',
                            },
                          ]}
                        >
                          <Input
                            className="ManageEvent__NameInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row
                      gutter={[24, 24]}
                      className="ManageEvent__DescriptionRow"
                    >
                      <Col
                        span={24}
                        className="ManageEvent__DescriptionColumn"
                      >
                        <Form.Item
                          className="ManageEvent__DescriptionFormItem"
                          name="description"
                          label={(
                            <Typography.Text
                              className="ManageEvent__DescriptionLabel ManageEvent__AllFormLabels"
                            >
                              Descrição
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              max: 1000,
                              message: 'Descrição deve possuir no máximo 1000 caracteres',
                            },
                          ]}
                        >
                          <Input.TextArea
                            className="ManageEvent__DescriptionInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row
                      gutter={[24, 24]}
                      className="ManageEvent__NameLinkRow"
                    >
                      <Col
                        span={12}
                        className="ManageEvent__NameLinkColumn"
                      >
                        <Form.Item
                          className="ManageEvent__NameLinkFormItem"
                          name="linkName"
                          label={(
                            <Typography.Text
                              className="ManageEvent__NameLinkLabel ManageEvent__AllFormLabels"
                            >
                              Descrição do link
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              max: 255,
                              message: 'O nome deve possuir no máximo 255 caracteres',
                            },
                          ]}
                        >
                          <Input
                            className="ManageEvent__NameLinkInput"
                          />
                        </Form.Item>
                      </Col>
                      <Col
                        span={12}
                        className="ManageEvent__LinkColumn"
                      >
                        <Form.Item
                          className="ManageEvent__LinkFormItem"
                          name="link"
                          tooltip="Informe um link iniciando com http ou https."
                          label={(
                            <Typography.Text
                              className="ManageEvent__LinkLabel ManageEvent__AllFormLabels"
                            >
                              URL do link
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              max: 1000,
                              message: 'O link deve possuir no máximo 1000 caracteres',
                            },
                          ]}
                        >
                          <Input
                            className="ManageEvent__LinkInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row
                      gutter={[24, 24]}
                      className="ManageEvent__DateRow"
                    >
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 5 }}
                        className="ManageEvent__DateColumn"
                      >
                        <Form.Item
                          className="ManageEvent__DateFormItem"
                          name="date"
                          label={(
                            <Typography.Text
                              className="ManageEvent__DateLabel ManageEvent__AllFormLabels"
                            >
                              Data
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Data é obrigatória',
                            },
                          ]}
                        >
                          <DatePicker
                            className="ManageEvent__DateInput"
                            format="DD/MM/YYYY"
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 5 }}
                        className="ManageEvent__StartTimeColumn"
                      >
                        <Form.Item
                          className="ManageEvent__StartTimeFormItem"
                          name="startTime"
                          dependencies={['endTime']}
                          label={(
                            <Typography.Text
                              className="ManageEvent__StartTimeLabel ManageEvent__AllFormLabels"
                            >
                              Início
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Início é obrigatório',
                            },
                            {
                              validator: (_rule, value): Promise<Error | void> => {
                                const endField = formInstance.getFieldValue('endTime');

                                if (!endField) {
                                  return Promise.resolve();
                                }

                                const start = moment(value, 'HH:mm').utc();
                                const end = moment(endField, 'HH:mm').utc();

                                if (start.isAfter(end)) {
                                  return Promise.reject(new Error('O início deve ocorrer antes do final'));
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <TimePicker
                            className="ManageEvent__StartTimeInput"
                            format="HH:mm"
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 5 }}
                        className="ManageEvent__EndTimeColumn"
                      >
                        <Form.Item
                          className="ManageEvent__EndTimeFormItem"
                          name="endTime"
                          dependencies={['startTime']}
                          label={(
                            <Typography.Text
                              className="ManageEvent__EndTimeLabel ManageEvent__AllFormLabels"
                            >
                              Final
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Final é obrigatório',
                            },
                            {
                              validator: (
                                _rule: any, value: moment.Moment,
                              ): Promise<Error | void> => {
                                const startField = formInstance.getFieldValue('startTime');

                                if (!startField) {
                                  return Promise.resolve();
                                }

                                const start = moment(startField, 'HH:mm').utc();
                                const end = moment(value, 'HH:mm').utc();

                                if (start.isAfter(end)) {
                                  return Promise.reject(new Error('O final deve ocorrer após o início'));
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <TimePicker
                            className="ManageEvent__EndTimeInput"
                            format="HH:mm"
                          />
                        </Form.Item>
                      </Col>

                      {
                        mode === 'EDIT' && userPermissions.includes('Cancelar evento')
                          ? (
                            <Col
                              xs={{ span: 24 }}
                              lg={{ span: 4 }}
                              className="ManageEvent__IsCancelledColumn"
                            >
                              <Form.Item
                                className="ManageEvent__IsCancelledFormItem"
                                name="isCancelled"
                                label={(
                                  <Typography.Text
                                    className="ManageEvent__IsCancelledLabel ManageEvent__AllFormLabels"
                                  >
                                    Evento cancelado?
                                  </Typography.Text>
                                )}
                                rules={[]}
                                valuePropName="checked"
                              >
                                <Switch
                                  size="default"
                                  unCheckedChildren="Não"
                                  checkedChildren="Sim"
                                  onChange={(value): void => {
                                    if (value) {
                                      formInstance.setFieldsValue({
                                        visible: false,
                                      });
                                    }
                                  }}
                                />
                              </Form.Item>
                            </Col>
                          )
                          : null
                      }

                      <Form.Item
                        noStyle
                        shouldUpdate
                      >
                        {
                          (formInstanceParameter: FormInstance): React.ReactNode => {
                            if (
                              mode === 'EDIT'
                              && userPermissions.includes('Cancelar evento')
                              && formInstanceParameter.getFieldValue('isCancelled') === true
                            ) {
                              return (
                                <Col
                                  xs={{ span: 24 }}
                                  lg={{ span: 5 }}
                                  className="ManageEvent__IsCancelledColumn"
                                >
                                  <Form.Item
                                    className="ManageEvent__IsCancelledFormItem"
                                    name="visible"
                                    label={(
                                      <>
                                        <Typography.Text
                                          className="ManageEvent__IsCancelledLabel ManageEvent__AllFormLabels"
                                        >
                                          Exibir evento?
                                        </Typography.Text>

                                        <Tooltip
                                          title={
                                            'Independentemente do valor deste campo, os '
                                            + 'eventos cancelados serão exibidos aos '
                                            + 'usuarios Coopercitrus na WEB.'
                                          }
                                        >
                                          <QuestionCircleOutlined className="ManageEvent__TooltipIcon" />
                                        </Tooltip>
                                      </>
                                    )}
                                    rules={[]}
                                    valuePropName="checked"
                                  >
                                    <Switch
                                      size="default"
                                      unCheckedChildren="Não"
                                      checkedChildren="Sim"
                                    />
                                  </Form.Item>
                                </Col>
                              );
                            }

                            return undefined;
                          }
                        }
                      </Form.Item>
                    </Row>

                    <Row
                      gutter={[24, 24]}
                      className="ManageEvent__LocationRow"
                    >
                      <Col
                        className="ManageEvent__CEPColumn"
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                      >
                        <Form.Item
                          className="ManageEvent__CEPFormItem"
                          label={(
                            <Typography.Text
                              className="ManageEvent__CEPLabel ManageEvent__AllFormLabels"
                            >
                              CEP
                            </Typography.Text>
                          )}
                          name="cep"
                          getValueFromEvent={
                            (eventt): string => eventt.target.value.replace(/[^0-9]/g, '')
                          }
                          validateFirst
                          rules={[
                            {
                              len: 8,
                              message: 'CEP deve ter exatamente 8 dígitos',
                            },
                            {
                              validator: validateCep,
                            },
                          ]}
                        >
                          <MaskedInput
                            guide={false}
                            mask={
                              (): Mask => {
                                return [
                                  /\d/, /\d/, /\d/, /\d/, /\d/,
                                  '-',
                                  /\d/, /\d/, /\d/,
                                ];
                              }
                            }
                            className="ant-input"
                            maxLength={9}
                          />
                        </Form.Item>
                        <Spin
                          className="ManageEvent__CepApiSpin"
                          spinning={cepApiLoading}
                        />
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ManageEvent__StateColumn"
                      >
                        <Form.Item
                          className="ManageEvent__StateFormItem"
                          name="state"
                          label={(
                            <Typography.Text
                              className="ManageEvent__StateLabel ManageEvent__AllFormLabels"
                            >
                              Estado
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Estado é obrigatório',
                            },
                          ]}
                        >
                          <Select
                            className="ManageEvent__StateInput"
                            allowClear
                            showSearch
                            loading={statesLoading}
                            options={
                              statesArray.map((element) => {
                                return ({
                                  ...element,
                                  label: `${element.nome} (${element.sigla})`,
                                  value: element.sigla,
                                });
                              })
                            }
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ManageEvent__CityColumn"
                      >
                        <Form.Item
                          className="ManageEvent__CityFormItem"
                          name="city"
                          dependencies={['state']}
                          label={(
                            <Typography.Text
                              className="ManageEvent__CityLabel ManageEvent__AllFormLabels"
                            >
                              Cidade
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Cidade é obrigatória',
                            },
                          ]}
                        >
                          <Select
                            className="ManageEvent__CityInput"
                            allowClear
                            showSearch
                            loading={citiesLoading}
                            options={
                              citiesArray.map((element) => {
                                return ({
                                  ...element,
                                  label: element.nome,
                                  value: element.nome,
                                });
                              })
                            }
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ManageEvent__DistrictColumn"
                      >
                        <Form.Item
                          name="district"
                          className="ManageEvent__DistrictFormItem"
                          label={(
                            <Typography.Text
                              className="ManageEvent__DistrictLabel ManageEvent__AllFormLabels"
                            >
                              Bairro
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              max: 255,
                              message: 'Bairro deve possuir no máximo 255 caracteres',
                            },
                          ]}
                        >
                          <Input
                            className="ManageEvent__DistrictInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row
                      gutter={[24, 24]}
                      className="ManageEvent__StreetAddressRow"
                    >
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 12 }}
                        className="ManageEvent__StreetAddressColumn"
                      >
                        <Form.Item
                          name="streetAddress"
                          className="ManageEvent__StreetAddressFormItem"
                          label={(
                            <Typography.Text
                              className="ManageEvent__StreetAddressLabel ManageEvent__AllFormLabels"
                            >
                              Rua
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Rua é obrigatória',
                            },
                            {
                              max: 1000,
                              message: 'Rua deve possuir no máximo 1000 caracteres',
                            },
                          ]}
                        >
                          <Input
                            className="ManageEvent__StreetAddressInput"
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ManageEvent__AddressComplementColumn"
                      >
                        <Form.Item
                          name="addressComplement"
                          className="ManageEvent__AddressComplementFormItem"
                          label={(
                            <Typography.Text
                              className="ManageEvent__AddressComplementLabel ManageEvent__AllFormLabels"
                            >
                              Complemento
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              max: 1000,
                              message: 'Complemento deve possuir no máximo 1000 caracteres',
                            },
                          ]}
                        >
                          <Input
                            className="ManageEvent__AddressComplementInput"
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 6 }}
                        className="ManageEvent__AddressNumberColumn"
                      >
                        <Form.Item
                          name="addressNumber"
                          className="ManageEvent__AddressNumberFormItem"
                          label={(
                            <Typography.Text
                              className="ManageEvent__AddressNumberLabel ManageEvent__AllFormLabels"
                            >
                              Número
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              max: 10,
                              message: 'Número deve possuir no máximo 10 caracteres',
                            },
                          ]}
                        >
                          <Input
                            className="ManageEvent__AddressNumberInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row
                      gutter={[24, 24]}
                      className="ManageEvent__SubsidiaryRow"
                    >
                      <Col
                        span={12}
                        className="ManageEvent__SubsidiaryColumn"
                      >
                        <Form.Item
                          className="ManageEvent__SubsidiaryFormItem"
                          name="subsidiary"
                          label={(
                            <Typography.Text
                              className="ManageEvent__SubsidiaryLabel ManageEvent__AllFormLabels"
                            >
                              Unidade de negócio responsável
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Unidade de negócio responsável é obrigatória. Caso não queira vincular unidades de negócio, selecione a opção "Nenhuma".',
                            },

                            {
                              validator: (_rule, value): Promise<Error | void> => {
                                if (value == null) {
                                  return Promise.resolve();
                                }

                                if (value.length > 1 && value.includes('NONE')) {
                                  return Promise.reject(new Error('Não é permitido selecionar "Nenhuma" junto de outras unidades.'));
                                }

                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Select
                            mode="multiple"
                            className="ManageEvent__SubsidiaryInput"
                            allowClear
                            showSearch
                            maxTagCount={4}
                            maxTagPlaceholder={
                              (omittedValues): React.ReactNode => {
                                return `+ ${omittedValues.length} selecionada(s)...`;
                              }
                            }
                            loading={subsidiaryLoading}
                            options={
                              subsidiaries.map((element: BranchCoopercitrusFromClient) => {
                                return ({
                                  label: element.name,
                                  value: element.id,
                                });
                              })
                                .concat([
                                  {
                                    label: 'Selecionar todas',
                                    value: 'ALL',
                                  },
                                  {
                                    label: 'Nenhuma',
                                    value: 'NONE',
                                  },
                                ])
                            }
                            optionFilterProp="label"
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        span={12}
                        className="ManageEvent__CultureColumn"
                      >
                        <Form.Item
                          className="ManageEvent__CultureFormItem"
                          name="culture"
                          label={(
                            <Typography.Text
                              className="ManageEvent__CultureLabel ManageEvent__AllFormLabels"
                            >
                              Cultura
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Cultura é obrigatória',
                            },
                            {
                              max: 255,
                              message: 'Cultura deve possuir no máximo 255 caracteres',
                            },
                          ]}
                        >
                          <Input
                            className="ManageEvent__CultureInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row
                      gutter={[24, 24]}
                      className="ManageEvent__CommentsRow"
                    >
                      <Col
                        span={24}
                        className="ManageEvent__CommentsColumn"
                      >
                        <Form.Item
                          className="ManageEvent__CommentsFormItem"
                          name="comments"
                          label={(
                            <Typography.Text
                              className="ManageEvent__CommentsLabel ManageEvent__AllFormLabels"
                            >
                              Observações
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              max: 1000,
                              message: 'Observações deve possuir no máximo 1000 caracteres',
                            },
                          ]}
                        >
                          <Input.TextArea
                            className="ManageEvent__CommentsInput"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row
                      className="ManageEvent__SubmitButtonRow"
                      justify="end"
                      gutter={[24, 24]}
                    >
                      <Col
                        className="ManageEvent__SubmitButtonColumn"
                        xs={{ span: 24 }}
                        sm={{ span: 8 }}
                        lg={{ span: 6 }}
                      >
                        <Form.Item
                          className="ManageEvent__SubmitButtonFormItem"
                        >
                          <Button
                            className="ManageEvent__SubmitButton"
                            type="primary"
                            loading={createRequestLoading}
                            htmlType="submit"
                            block
                          >
                            Salvar
                          </Button>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Form>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

ManageEvent.propTypes = {};
ManageEvent.defaultProps = {};

export default ManageEvent;
